import React from "react";

/**
 * Anivaze v4 — vendored open-source icon registry (Tabler MIT + Lucide ISC;
 * see assets/icons/LICENSES.md).
 *
 * Real symbols for real subjects: motors, coils, brains, hands, cables,
 * servers… loaded as raw SVG at build time, tinted to the theme, and matched to
 * diagram labels by keyword so every pack's nodes show the actual thing instead
 * of an abstract dot. Rendering replaces `currentColor` with a concrete hex so
 * the browser AND the headless rasterizer (sharp/librsvg) agree — deterministic.
 */

// Vite/vitest both support eager glob raw imports.
const RAW: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob("./assets/icons/*.svg", { query: "?raw", import: "default", eager: true }) as Record<string, string>,
  ).map(([path, src]) => [path.replace(/^.*\/(.+)\.svg$/, "$1"), src]),
);

export const ICON_KEYS: string[] = Object.keys(RAW).sort();

/** Inner markup of an icon (outer <svg> stripped), tinted to `color`. */
export function iconMarkup(key: string, color: string): string | null {
  const src = RAW[key];
  if (!src) return null;
  const inner = src.replace(/^[\s\S]*?<svg[^>]*>/, "").replace(/<\/svg>\s*$/, "");
  return inner.replace(/currentColor/g, color);
}

/** Keyword → icon key. First match wins; longest keywords checked first. */
const KEYWORD_ENTRIES: Array<[string, string]> = [
  // electrical / machines
  ["inductor", "circuit-inductor"], ["coil", "circuit-inductor"],
  ["motor", "circuit-motor"], ["resistor", "circuit-resistor"],
  ["voltmeter", "circuit-voltmeter"], ["voltage", "circuit-voltmeter"], ["volt", "circuit-voltmeter"],
  ["battery", "circuit-battery"], ["switch", "circuit-switch-open"],
  ["engine", "engine"], ["magnet", "magnet"], ["plug", "plug"],
  ["electric", "bolt"], ["current", "bolt"], ["energy", "bolt"], ["power", "bolt"], ["charge", "bolt"],
  ["bulb", "circuit-bulb"], ["lamp", "circuit-bulb"], ["light", "circuit-bulb"],
  // tech / networking
  ["kubernetes", "topology-star-3"], ["cluster", "topology-star-3"], ["topology", "topology-star-3"],
  ["database", "database"], ["storage", "database"], ["disk", "database"],
  ["server", "server"], ["backend", "server"], ["host", "server"], ["pod", "server"],
  ["cloud", "cloud"], ["processor", "cpu"], ["chip", "cpu"], ["cpu", "cpu"],
  ["laptop", "device-laptop"], ["computer", "device-laptop"], ["browser", "device-laptop"], ["client", "device-laptop"],
  ["mobile", "device-mobile"], ["phone", "device-mobile"], ["app", "device-mobile"],
  ["router", "router"], ["wifi", "wifi"], ["dns", "network"], ["network", "network"], ["node", "network"],
  ["terminal", "terminal-2"], ["shell", "terminal-2"], ["command", "terminal-2"],
  ["code", "code"], ["script", "code"], ["api", "api"],
  ["internet", "world"], ["world", "world"], ["globe", "world"], ["global", "world"], ["web", "world"],
  ["cable", "cable"], ["wire", "cable"], ["fiber", "cable"],
  ["signal", "waves"], ["wave", "waves"], ["antenna", "antenna"], ["broadcast", "antenna"], ["satellite", "orbit"], ["orbit", "orbit"],
  // science / bio
  ["neuron", "brain"], ["brain", "brain"], ["mind", "brain"], ["memory", "brain"],
  ["cardiac", "heart"], ["heart", "heart"], ["dna", "dna-2"], ["gene", "dna-2"],
  ["microscope", "microscope"], ["chemistry", "flask"], ["flask", "flask"], ["lab", "flask"],
  ["plant", "leaf"], ["leaf", "leaf"], ["photosynthesis", "leaf"],
  ["virus", "virus"], ["bacteria", "virus"], ["doctor", "stethoscope"], ["health", "stethoscope"],
  // everyday / business
  ["hand", "hand"], ["touch", "hand"], ["person", "user"], ["user", "user"], ["people", "users"], ["team", "users"], ["crowd", "users"],
  ["book", "book"], ["read", "book"], ["school", "school"], ["learn", "school"], ["idea", "bulb"],
  ["dollar", "currency-dollar"], ["price", "currency-dollar"], ["cost", "currency-dollar"], ["salary", "currency-dollar"],
  ["money", "coin"], ["coin", "coin"], ["invest", "chart-line"], ["stock", "chart-line"], ["market", "chart-line"], ["chart", "chart-line"], ["growth", "chart-line"],
  ["clock", "clock"], ["time", "clock"], ["solar", "sun"], ["sun", "sun"],
  ["water", "droplet"], ["rain", "droplet"], ["droplet", "droplet"],
  ["fire", "flame"], ["heat", "flame"], ["flame", "flame"], ["burn", "flame"],
  ["car", "car"], ["rocket", "rocket"], ["launch", "rocket"], ["factory", "building-factory-2"],
  ["truck", "truck"], ["shipping", "truck"], ["delivery", "truck"],
  ["settings", "settings"], ["gear", "settings"], ["config", "settings"],
  ["lock", "lock"], ["encrypt", "lock"], ["security", "shield"], ["shield", "shield"], ["secure", "shield"],
  ["key", "key"], ["password", "key"], ["token", "key"],
  ["mail", "mail"], ["email", "mail"], ["search", "search"], ["query", "search"],
  ["watch", "eye"], ["monitor", "eye"], ["observe", "eye"], ["eye", "eye"],
  ["risk", "alert-triangle"], ["danger", "alert-triangle"], ["warning", "alert-triangle"], ["alert", "alert-triangle"],
  ["success", "circle-check"], ["done", "circle-check"], ["check", "circle-check"], ["deploy", "circle-check"],
  ["trade", "arrows-exchange"], ["exchange", "arrows-exchange"], ["swap", "arrows-exchange"],
  ["balance", "scale"], ["scale", "scale"], ["compare", "scale"], ["weigh", "scale"],
];
const KEYWORD_MAP: Array<[string, string]> = [...KEYWORD_ENTRIES].sort((a, b) => b[0].length - a[0].length);

/** Best icon for a diagram label ("Web Server" → server). Null when nothing fits. */
export function iconForLabel(label: string): string | null {
  const s = label.toLowerCase();
  for (const [kw, key] of KEYWORD_MAP) {
    if (s.includes(kw) && RAW[key]) return key;
  }
  return null;
}

/** Theme-tinted icon, centered at (cx, cy), `size` px tall (source is 24-unit). */
export const Icon: React.FC<{ k: string; cx: number; cy: number; size: number; color: string; opacity?: number }> = ({
  k, cx, cy, size, color, opacity = 1,
}) => {
  const inner = iconMarkup(k, color);
  if (!inner) return null;
  const s = size / 24;
  return (
    <g
      transform={`translate(${cx - size / 2} ${cy - size / 2}) scale(${s})`}
      opacity={opacity}
      // Tabler/Lucide put the stroke on the OUTER <svg> (which we strip) and let
      // children inherit — so the wrapper must carry the presentation attrs.
      stroke={color}
      strokeWidth={2}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: inner }}
    />
  );
};
